import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { LlmApi, LlmProvider } from '../api/llm'

/**
 * LLM Settings: the companies the platform can call through Azure AI Foundry, and the models it
 * may call there (admin-site System).
 *
 * The Owner alone can open this -- the API refuses everyone else, and this screen only hides
 * what the API would refuse anyway. There is no credential on this page: the API reaches Foundry
 * with its own managed identity, and the one thing worth saying about that is whether Azure will
 * currently issue it a token, which is the line at the top.
 */
export function LlmSettings({ api }: { api: LlmApi }) {
  const setup = useQuery({ queryKey: ['llm'], queryFn: () => api.read() })

  if (setup.isPending) {
    return <p>Reading the settings...</p>
  }

  if (setup.isError) {
    return (
      <p className="border-l-2 border-red-700 pl-3 text-red-700">
        {setup.error.message} Only the Owner may read these settings.
      </p>
    )
  }

  return (
    <>
      <FoundryAccess api={api} />

      <div className="mt-6 space-y-6">
        {setup.data.providers.map((provider) => (
          <ProviderCard key={provider.code} api={api} provider={provider} />
        ))}
      </div>
    </>
  )
}

/**
 * Whether Azure will issue the API a Foundry token.
 *
 * Its own query because it is its own round trip, and a slow answer from Azure should not hold
 * up the list of providers underneath it.
 */
function FoundryAccess({ api }: { api: LlmApi }) {
  const access = useQuery({ queryKey: ['llm', 'access'], queryFn: () => api.access() })

  if (access.isPending) {
    return <p className="text-muted">Checking that Azure will let the API into Foundry...</p>
  }

  if (access.isError) {
    return <p className="break-words text-red-700">{access.error.message}</p>
  }

  return (
    <p className={access.data.granted ? 'break-words text-muted' : 'break-words text-red-700'}>
      {access.data.granted ? 'Signed in to Azure as the API. ' : 'The API cannot reach Foundry. '}
      {access.data.detail} There are no keys to store: the API calls Foundry as itself.
    </p>
  )
}

function ProviderCard({ api, provider }: { api: LlmApi; provider: LlmProvider }) {
  const client = useQueryClient()
  const [endpoint, setEndpoint] = useState(provider.endpoint ?? '')
  const [name, setName] = useState('')
  const [version, setVersion] = useState('')

  // One mutation per card rather than one per button. Every action here ends the same way --
  // the whole setup is read again -- so the difference between them is the call itself, and
  // the failure of whichever ran last is what the card has to show.
  const act = useMutation({
    mutationFn: (action: () => Promise<void>) => action(),
    onSuccess: async () => {
      setName('')
      setVersion('')
      await client.invalidateQueries({ queryKey: ['llm'] })
    },
  })

  const running = act.isPending

  return (
    <section
      aria-labelledby={`provider-${provider.code}`}
      className="rounded-lg border border-ink-100 bg-card p-6"
    >
      <h2 id={`provider-${provider.code}`} className="font-heading text-h3 font-bold text-heading">
        {provider.name}
      </h2>

      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          act.mutate(() => api.saveProvider(provider.code, provider.name, endpoint.trim() || null))
        }}
      >
        <label className="flex min-w-0 grow flex-col gap-1">
          <span className="text-muted">Foundry endpoint</span>
          <input
            type="url"
            value={endpoint}
            placeholder="https://ai-exam-api-dev.services.ai.azure.com"
            onChange={(event) => setEndpoint(event.target.value)}
            className="w-full rounded-md border border-ink-100 bg-page px-3 py-2"
          />
        </label>
        <button type="submit" disabled={running} className={quiet}>
          Save endpoint
        </button>
      </form>

      <h3 className="mt-6 font-heading text-h4 font-bold text-heading">Models</h3>

      {provider.models.length === 0 ? (
        <p className="mt-2 text-muted">None yet.</p>
      ) : (
        <ul className="mt-2 divide-y divide-ink-100">
          {provider.models.map((model) => (
            <li key={model.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="grow break-words">
                {model.name} <span className="text-muted">version {model.version}</span>
                {!model.enabled && <span className="text-muted"> &mdash; disabled</span>}
              </span>
              <button
                type="button"
                disabled={running}
                className={quiet}
                onClick={() =>
                  act.mutate(() =>
                    api.describeModel(model.id, model.name, model.version, !model.enabled),
                  )
                }
              >
                {model.enabled ? 'Disable' : 'Enable'}
              </button>
              <button
                type="button"
                disabled={running}
                className={quiet}
                onClick={() => act.mutate(() => api.removeModel(model.id))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          act.mutate(() => api.addModel(provider.code, name, version))
        }}
      >
        <label className="flex min-w-0 grow flex-col gap-1">
          <span className="text-muted">Model</span>
          <input
            value={name}
            placeholder="gpt-5"
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded-md border border-ink-100 bg-page px-3 py-2"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-muted">Version</span>
          <input
            value={version}
            placeholder="2026-04-01"
            onChange={(event) => setVersion(event.target.value)}
            className="w-full rounded-md border border-ink-100 bg-page px-3 py-2"
          />
        </label>
        <button
          type="submit"
          disabled={running || name.trim().length === 0 || version.trim().length === 0}
          className={quiet}
        >
          Add model
        </button>
      </form>

      {/* Rendered only when there is something to say. An always-present alert region is an
          empty landmark a screen reader still meets, and role=alert announces on insertion --
          assertively, which is right: this is the answer to something the Owner just did. */}
      {act.isError && (
        <p role="alert" className="mt-3 text-red-700">
          {act.error.message}
        </p>
      )}
    </section>
  )
}

const quiet =
  'rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'
